import { useCallback, useEffect, useState } from "react";
import { Buton, cn, Iskelet, Kart, MetinAlani, Secim } from "@koclukpanel/ui";
import { EN_AZ_GEREKCE } from "@koclukpanel/contracts";
import {
  ApiHatasi,
  degisiklikHakkim,
  degisiklikTalebiAc,
  degisiklikTaleplerim,
  type DegisiklikNedeni,
  type DegisiklikTalebi as Talep,
  type HakDurumu,
  type TalepDurumu,
} from "./api";

/**
 * PROGRAM DEGISIKLIK TALEBI — F-SCH-11, matris §5.6
 *
 * ================================================================
 * DUGMEYI SUNUCUNUN CEVABI ACIYOR
 * ================================================================
 * Ekran "bu ay talep actim mi" sorusunu kendi takvimine gore
 * HESAPLAMIYOR. Hesaplasaydi saati yanlis olan bir telefonda ogrenci
 * hakkini kullanamamis ya da fazladan kullanmis gorunurdu; sinir
 * zaten veritabani kisitiyla uygulandigi icin ekran yalan soylerdi.
 *
 * Ogrenci kimligi de gonderilmiyor — sunucu oturumdan aliyor
 * (`talepAc` parametre almiyor). Gonderilseydi bir ogrenci
 * baskasinin aylik hakkini harcayabilirdi.
 */

const NEDEN_ADI: Record<DegisiklikNedeni, string> = {
  SCHOOL_LOAD: "Okul veya sınav yoğunluğu",
  HEALTH: "Sağlık",
  FAMILY: "Ailevi sebep",
  DID_NOT_UNDERSTAND: "Konuyu anlamadım",
  RESOURCE_ISSUE: "Kaynak sorunu",
  TOO_HEAVY: "Program bana ağır geldi",
  TOO_LIGHT: "Program bana hafif geldi",
  OTHER: "Diğer",
};

const DURUM_ADI: Record<TalepDurumu, string> = {
  PENDING: "Uzmanın yanıtı bekleniyor",
  ACCEPTED: "Kabul edildi",
  PARTIALLY_ACCEPTED: "Kısmen kabul edildi",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal edildi",
};

const DURUM_SINIFI: Record<TalepDurumu, string> = {
  PENDING: "bg-uyari-500/15 text-uyari-600",
  ACCEPTED: "bg-basarili-500/15 text-onay-700",
  PARTIALLY_ACCEPTED: "bg-basarili-500/15 text-onay-700",
  REJECTED: "bg-hata-500/15 text-hata-700",
  CANCELLED: "bg-notr-100 text-notr-600",
};

/**
 * "1 Eylül'de — 4 gün kaldı" cumlesi.
 *
 * Gun farki TAM GUN SINIRINDA olculuyor, ms farkiyla degil: 23:50 ile
 * ertesi sabah 00:10 arasi yirmi dakika ama BASKA gunler ve ogrenci
 * "bugun" ile "yarin"i karistirmamali.
 *
 * Tarihin kendisi SUNUCUDAN (`sonrakiHakAt`); burada yalnizca
 * bicimlendiriliyor.
 */
function acilisMetni(sonrakiHakAt: string): string {
  const hedef = new Date(sonrakiHakAt);
  const gun = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const kalan = Math.round((gun(hedef) - gun(new Date())) / 86_400_000);
  const tarih = hedef.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });

  // "0 gun kaldi" dogru ama kimse oyle konusmuyor.
  if (kalan <= 0) return `${tarih}'de, bugün`;
  if (kalan === 1) return `${tarih}'de, yarın`;
  return `${tarih}'de, ${kalan} gün sonra`;
}

export function DegisiklikTalebi() {
  const [hak, setHak] = useState<HakDurumu | null>(null);
  const [talepler, setTalepler] = useState<Talep[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);

  const [formAcik, setFormAcik] = useState(false);
  const [neden, setNeden] = useState<DegisiklikNedeni>("TOO_HEAVY");
  const [metin, setMetin] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderimHatasi, setGonderimHatasi] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const [h, l] = await Promise.all([degisiklikHakkim(), degisiklikTaleplerim()]);
      setHak(h);
      setTalepler(l.talepler);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Bilgiler alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const gonder = async () => {
    setGonderiliyor(true);
    setGonderimHatasi(null);
    try {
      await degisiklikTalebiAc({ reason: neden, reasonText: metin.trim() });
      setMetin("");
      setFormAcik(false);
      await yukle();
    } catch (err) {
      setGonderimHatasi(err instanceof ApiHatasi ? err.message : "Talep gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  if (yukleniyor && !hak) {
    return <Iskelet satir={4} />;
  }

  if (hata) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
        <Buton gorunum="ikincil" onClick={() => void yukle()}>
          Tekrar dene
        </Buton>
      </div>
    );
  }

  const kisa = metin.trim().length < EN_AZ_GEREKCE;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-notr-900 text-lg font-semibold">Program değişikliği</h1>
        <p className="text-notr-600 mt-1 text-sm">
          Ayda bir kez programının değiştirilmesini isteyebilirsin. Uzmanın seninle konuşup karar
          verir.
        </p>
      </div>

      {/* Hak durumu — METIN SUNUCUNUN CEVABINDAN uretiliyor. */}
      {hak && <HakKarti hak={hak} />}

      {hak?.acabilirMi && !formAcik && (
        <Buton boyut="genis" onClick={() => setFormAcik(true)}>
          Değişiklik iste
        </Buton>
      )}

      {formAcik && (
        <form
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!kisa) void gonder();
          }}
        >
          {/*
            HAM `<select>` YERİNE `Secim`.

            `Secim`in kendi gerekçesi mevcut kutuların TOPLU
            çevrilmemesini söylüyor ve haklı: çalışan, erişilebilir bir
            SÜZGECİ yalnızca tekdüzelik için değiştirmek doğru değil.
            Ama aynı gerekçe istisnasını da yazıyor — eksik olan tek şey
            hata durumu ve o "kayıt formlarında gerekiyor". Burası bir
            kayıt formu: gönderilen şey öğrencinin aylık tek hakkı.
          */}
          <Secim
            etiket="Sebep"
            value={neden}
            onChange={(e) => setNeden(e.target.value as DegisiklikNedeni)}
          >
            {(Object.keys(NEDEN_ADI) as DegisiklikNedeni[]).map((n) => (
              <option key={n} value={n}>
                {NEDEN_ADI[n]}
              </option>
            ))}
          </Secim>

          {/*
            Sayac `yardim` olarak veriliyor: MetinAlani onu etiketin
            ICINE DEGIL, `aria-describedby` ile bagliyor. Icinde
            oldugunda alanin erisilebilir adi "Ne değişsin, neden?En az
            20 karakter — 16 tane daha" oluyordu: ekran okuyucu her tusa
            basista alanin adini yeniden okurdu.

            Sayac ayrica ESIGE KADAR gosteriliyor. "Cok kisa" hatasini
            gonderdikten SONRA gormek, ogrenciyi yazdigini silip bastan
            yazmaya iter.
          */}
          <MetinAlani
            etiket="Ne değişsin, neden?"
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Örnek: Salı ve perşembe okulda deneme sınavı var, o günlerin matematik görevleri hafta sonuna alınabilir mi?"
            yardim={
              <span className={cn(kisa && "text-uyari-600")}>
                {kisa
                  ? `En az ${EN_AZ_GEREKCE} karakter — ${EN_AZ_GEREKCE - metin.trim().length} tane daha`
                  : "Uzmanın bunu okuyup karar verecek."}
              </span>
            }
          />

          <div className="flex gap-2">
            <Buton type="submit" disabled={kisa || gonderiliyor}>
              {gonderiliyor ? "Gönderiliyor…" : "Gönder"}
            </Buton>
            <Buton gorunum="sessiz" type="button" onClick={() => setFormAcik(false)}>
              Vazgeç
            </Buton>
          </div>

          {/*
            ================================================================
            GERİ DÖNÜŞSÜZLÜK GÖNDERMEDEN ÖNCE SÖYLENİYOR — ÇİZİMDEN
            ================================================================
            Reddedilen talep hakkı geri getirmiyor (bilinçli: aksi hâlde
            öğrenci aynı talebi tekrar tekrar açarak sınırsız hak elde
            ederdi). Bunu SONRA öğrenmek, öğrencinin yarım yamalak
            yazdığı bir isteği göndermesine ve ayı kaybetmesine yol
            açıyor. Uyarı düğmenin altında çünkü okunması gereken an
            tam olarak basmadan hemen önce.
          */}
          <p className="text-notr-600 text-xs">
            Gönderdikten sonra bu ay yeni istek açamazsın — reddedilse bile.
          </p>

          {gonderimHatasi && (
            <p role="alert" className="text-hata-500 text-sm">
              {gonderimHatasi}
            </p>
          )}
        </form>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-notr-900 text-sm font-semibold">Önceki isteklerin</h2>
        {talepler.length === 0 ? (
          <p className="text-notr-600 text-sm">Henüz bir istek göndermedin.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {talepler.map((t) => (
              <Kart key={t.id} olarak="li" className="p-3">
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="text-notr-900 block text-sm font-medium">
                      {NEDEN_ADI[t.reason]}
                    </span>
                    <span className="text-notr-600 mt-0.5 block text-sm">{t.reasonText}</span>
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                      DURUM_SINIFI[t.status],
                    )}
                  >
                    {DURUM_ADI[t.status]}
                  </span>
                </div>

                {/*
                  Uzmanin yaniti VARSA gosteriliyor. Reddedilen bir
                  talepte gerekce gormemek, ogrenciyi ayni talebi
                  tekrar acmaya iter — ki hakki geri gelmedigi icin
                  bunu yapamaz ve sistem "sessizce hayir diyen" bir
                  sey olur.
                */}
                {t.coachResponse && (
                  <p className="rounded-alan bg-notr-50 text-notr-700 mt-2 p-2 text-sm">
                    <strong className="font-medium">Uzmanın:</strong> {t.coachResponse}
                  </p>
                )}

                {t.isExtraGrant && (
                  <p className="text-notr-600 mt-2 text-xs">
                    Bu istek, uzmanının tanıdığı ek hakla açıldı.
                  </p>
                )}
              </Kart>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Hak durumu karti.
 *
 * Uc ayri hal ve ucu de FARKLI sey soyluyor:
 *   hakki var                  -> istek acabilir
 *   normal hak dolu, ek hak var-> uzman ozel olarak yer acmis
 *   ay dolu                    -> ne zaman acilacagi ve NE YAPACAGI yazili
 */
function HakKarti({ hak }: { hak: HakDurumu }) {
  if (hak.acabilirMi && hak.kullanilabilirEkHak !== null && hak.normalKullanildi) {
    return (
      <p className="rounded-alan bg-basarili-500/10 text-notr-800 p-3 text-sm">
        Bu ayki hakkını kullandın ama uzmanın sana <strong>ek bir hak</strong> tanıdı. Yeni bir
        istek gönderebilirsin.
      </p>
    );
  }

  if (hak.acabilirMi) {
    return (
      <p className="rounded-alan bg-notr-50 text-notr-700 p-3 text-sm">
        Bu ay <strong>bir</strong> değişiklik isteği gönderebilirsin.
      </p>
    );
  }

  /*
    ================================================================
    "HAKKIN YOK" DEMEK YETMEZ — NE YAPACAGINI DA SOYLEMELI
    ================================================================
    Reddedilen ya da iptal edilen talep hakki geri getirmiyor
    (bilincli: aksi halde ogrenci reddedilen talebi tekrar tekrar
    acarak sinirsiz hak elde ederdi). Ama bu, haksiz bir redde
    ugrayan ogrenciyi cikissiz birakmamali; cikis yolu var ve
    burada yaziyor.
  */
  return (
    <div className="rounded-alan bg-uyari-500/10 text-notr-800 p-3 text-sm">
      {/*
        ================================================================
        TARİH VE GERİ SAYIM — ÇİZİMDEN, AMA SUNUCUNUN VERİSİYLE
        ================================================================
        Tuval (`ProgramIstegi.dc.html`) "1 Eylül'de yeni hakkın
        açılacak — 4 gün kaldı" diyor. Bu cümle bugüne kadar
        yazılamıyordu: `HakDurumu` tarihi taşımıyordu ve istemcide
        hesaplamak bu dosyanın açılış kuralını çiğnerdi — saati yanlış
        bir telefon yanlış tarih gösterir.

        Sunucu artık `sonrakiHakAt` döndürüyor, yani tarih ÖLÇÜLMÜŞ bir
        veri. Kalan gün sayısı ondan türetiliyor ve tam gün sınırında
        hesaplanıyor: 23:50 ile ertesi sabah 00:10 arası yirmi dakika
        ama BAŞKA günler.

        "Bugün" ve "yarın" ayrı yazılıyor — "0 gün kaldı" doğru ama
        kimse öyle konuşmuyor.
      */}
      <p>
        <strong>{acilisMetni(hak.sonrakiHakAt)}</strong> yeni hakkın açılacak.
      </p>
      {/*
        ÇİZİMİN CÜMLESİ: çıkış yolu bir KANAL adıyla söyleniyor.
        "Uzmanına söyle" doğruydu ama nereden söyleyeceğini
        söylemiyordu; öğrenci uygulamanın içinde arayıp bulamayınca
        hiç söylemiyor. "İstek ve yardım" ayrı bir kanal ve aylık
        hakkı HARCAMIYOR — bunu yazmak da gerekiyor, yoksa öğrenci
        oradan yazmanın da bir bedeli olduğunu sanır.
      */}
      <p className="mt-2">
        Acil bir durum varsa <strong>Hesabım → İstek ve yardım</strong>'dan yazabilirsin. O ayrı bir
        kanal ve bu hakkını harcamıyor; uzmanın istersen sana bu ay için ek hak da tanıyabilir.
      </p>
    </div>
  );
}
